import { useState, useEffect } from "react";
import Sidebar from './layout/Sidebar';
import Header from './layout/Header';
import { useAuth } from '../hooks';

const FRAMEWORKS = [
  {
    id: "mica", code: "MiCA", status: "ACTIVE",
    name: "Markets in Crypto-Assets Regulation",
    desc: "Unified licensing framework for CASPs, stablecoin issuers, and crypto-asset offerors across the EU and EEA.",
    heat: 94, trend: [68, 72, 75, 79, 83, 88, 91, 94],
    drivers: ["CASP licensing deadline Jul 2026", "iXBRL whitepaper format live Dec 2025", "ESMA transitional period supervision active"],
    authorities: ["ESMA", "EBA"],
    enforcementDate: "30 Dec 2024",
    scope: "EU-wide + EEA",
    nextDeadline: "01 Jul 2026",
    deadlineLabel: "CASP transitional period ends",
    complexity: "High",
    phase: "Enforcement",
    tracks: ["casp", "exchange", "custody"],
    url: "https://www.esma.europa.eu/esmas-activities/digital-finance-and-innovation/markets-crypto-assets-regulation-mica",
  },
  {
    id: "dora", code: "DORA", status: "ACTIVE",
    name: "Digital Operational Resilience Act",
    desc: "ICT risk management, incident reporting, third-party oversight, and digital resilience testing for all EU financial entities.",
    heat: 81, trend: [55, 60, 65, 68, 70, 74, 78, 81],
    drivers: ["EBA DORA critical third-party oversight from 2026", "Infringement procedures against Latvia & others", "ICT incident reporting enforcement active"],
    authorities: ["ESMA", "EBA", "EIOPA"],
    enforcementDate: "17 Jan 2025",
    scope: "EU-wide",
    nextDeadline: "17 Jan 2026",
    deadlineLabel: "Critical 3rd-party oversight begins",
    complexity: "High",
    phase: "Enforcement",
    tracks: ["casp", "bank", "insurer", "payment", "exchange"],
    url: "https://www.eba.europa.eu/regulation-and-policy/digital-operational-resilience-act-dora",
  },
  {
    id: "aml", code: "AML/CFT", status: "ACTIVE",
    name: "AML Regulation & 6th Directive",
    desc: "Strengthened AML/CFT framework with direct EU regulation, enhanced beneficial ownership rules, and new AMLA authority.",
    heat: 76, trend: [60, 63, 65, 66, 68, 71, 73, 76],
    drivers: ["AMLA authority operational 2025", "Travel Rule (TFR) enforcement live Dec 2024", "CASP AML obligations under MiCA active"],
    authorities: ["EBA", "AMLA", "National FIUs"],
    enforcementDate: "01 Jan 2027",
    scope: "EU-wide",
    nextDeadline: "01 Jan 2027",
    deadlineLabel: "AMLA full supervisory powers",
    complexity: "High",
    phase: "Implementation",
    tracks: ["casp", "bank", "payment", "exchange", "custody"],
    url: "https://www.eba.europa.eu/regulation-and-policy/anti-money-laundering-and-countering-financing-terrorism",
  },
  {
    id: "psd3", code: "PSD3/PSR", status: "PLANNED",
    name: "Payment Services Directive 3",
    desc: "Modernised open banking framework with stronger SCA, expanded TPP access, and new Payment Services Regulation for direct application.",
    heat: 58, trend: [30, 35, 38, 42, 45, 50, 54, 58],
    drivers: ["European Commission adoption expected 2026", "PSR to replace PSD2 directly without national transposition", "Open finance (FIDA) moving in parallel"],
    authorities: ["EBA", "European Commission"],
    enforcementDate: "Q2 2026 (Est.)",
    scope: "EU-wide",
    nextDeadline: "01 Jun 2026",
    deadlineLabel: "Commission adoption expected",
    complexity: "Medium",
    phase: "Legislative",
    tracks: ["payment", "bank", "emoney"],
    url: "https://finance.ec.europa.eu/regulation-and-supervision/financial-services-legislation/implementing-and-delegated-acts/payment-services-directive_en",
  },
  {
    id: "gdpr", code: "GDPR", status: "ACTIVE",
    name: "General Data Protection Regulation",
    desc: "Comprehensive data protection law covering processing, controller obligations, cross-border transfers and enforcement across all sectors.",
    heat: 47, trend: [45, 46, 44, 47, 45, 46, 48, 47],
    drivers: ["Cross-border enforcement DPC coordination ongoing", "AI Act data provisions interact with GDPR", "EDPB cookie guidelines enforcement"],
    authorities: ["EDPB", "National DPAs"],
    enforcementDate: "25 May 2018",
    scope: "EU-wide + Extraterritorial",
    nextDeadline: "Ongoing",
    deadlineLabel: "Continuous enforcement",
    complexity: "Medium",
    phase: "Mature",
    tracks: ["casp", "bank", "payment", "exchange", "custody", "insurer", "emoney"],
    url: "https://www.edpb.europa.eu",
  },
  {
    id: "nis2", code: "NIS2", status: "ACTIVE",
    name: "Network & Information Security Directive 2",
    desc: "Cybersecurity obligations for essential and important entities. Incident reporting, supply chain security, and management accountability.",
    heat: 52, trend: [40, 44, 46, 48, 50, 51, 51, 52],
    drivers: ["National transposition ongoing — some states delayed", "ENISA implementing acts published", "Overlap with DORA for financial sector"],
    authorities: ["ENISA", "National CSIRTs"],
    enforcementDate: "17 Oct 2024",
    scope: "EU-wide",
    nextDeadline: "01 Apr 2026",
    deadlineLabel: "Full national enforcement",
    complexity: "Medium",
    phase: "Transposition",
    tracks: ["casp", "bank", "payment", "exchange"],
    url: "https://www.enisa.europa.eu/topics/cybersecurity-policy/nis-directive-new",
  },
  {
    id: "mifid", code: "MiFID II", status: "ACTIVE",
    name: "Markets in Financial Instruments Directive II",
    desc: "Investor protection, best execution, product governance, and reporting obligations for investment firms and trading venues.",
    heat: 38, trend: [40, 39, 38, 37, 38, 39, 38, 38],
    drivers: ["MiFID II/MiFIR review applying 2025", "ESMA clarification statements on equity transparency", "Commodity derivatives RTS pending"],
    authorities: ["ESMA"],
    enforcementDate: "03 Jan 2018",
    scope: "EU-wide + EEA",
    nextDeadline: "30 Jun 2026",
    deadlineLabel: "MiFIR review RTS adoption",
    complexity: "High",
    phase: "Mature",
    tracks: ["exchange", "bank"],
    url: "https://www.esma.europa.eu/regulation/trading/mifid-ii-and-mifir",
  },
  {
    id: "fida", code: "FIDA", status: "PLANNED",
    name: "Financial Data Access Regulation",
    desc: "Open finance framework giving customers control over their financial data. Extends open banking beyond payment accounts.",
    heat: 31, trend: [10, 14, 18, 20, 24, 26, 29, 31],
    drivers: ["Commission proposal under negotiation", "PSD3 and FIDA moving in parallel", "EIOPA pension data scope under discussion"],
    authorities: ["European Commission", "EBA"],
    enforcementDate: "2027+ (Est.)",
    scope: "EU-wide",
    nextDeadline: "31 Dec 2026",
    deadlineLabel: "Trilogue completion expected",
    complexity: "Medium",
    phase: "Legislative",
    tracks: ["bank", "payment", "emoney"],
    url: "https://finance.ec.europa.eu/regulation-and-supervision/financial-services-legislation/implementing-and-delegated-acts/financial-data-access_en",
  },
];

const TRACKS = [
  { id: "all",      label: "All Frameworks"      },
  { id: "casp",     label: "Crypto-Asset SP"      },
  { id: "payment",  label: "Payment Institution"  },
  { id: "bank",     label: "Bank / Credit"        },
  { id: "emoney",   label: "E-Money Institution"  },
  { id: "exchange", label: "Exchange / Trading"   },
  { id: "custody",  label: "Custody / Wallet"     },
];

const DEADLINES = [
  { date: "2026-03-24", label: "NIS2: Full national enforcement",                          framework: "NIS2",    heat: 52, urgent: true  },
  { date: "2026-04-17", label: "DORA: EBA critical 3rd-party oversight begins",           framework: "DORA",    heat: 81, urgent: true  },
  { date: "2026-06-01", label: "PSD3/PSR: Commission adoption expected",                  framework: "PSD3",    heat: 58, urgent: false },
  { date: "2026-06-30", label: "MiFID II: MiFIR review RTS adoption",                     framework: "MiFID II",heat: 38, urgent: false },
  { date: "2026-07-01", label: "MiCA: CASP transitional period ends",                     framework: "MiCA",    heat: 94, urgent: true  },
  { date: "2026-12-31", label: "FIDA: Trilogue completion expected",                      framework: "FIDA",    heat: 31, urgent: false },
  { date: "2027-01-01", label: "AML/CFT: AMLA full supervisory powers",                   framework: "AML/CFT", heat: 76, urgent: false },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function heatColor(score) {
  if (score >= 85) return { bar: "#dc2626", bg: "#fef2f2", text: "#dc2626", border: "#fecaca", trackBg: "#fee2e2" };
  if (score >= 65) return { bar: "#ea580c", bg: "#fff7ed", text: "#ea580c", border: "#fed7aa", trackBg: "#ffedd5" };
  if (score >= 45) return { bar: "#ca8a04", bg: "#fefce8", text: "#a16207", border: "#fde68a", trackBg: "#fef9c3" };
  return               { bar: "#16a34a", bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", trackBg: "#dcfce7" };
}

function heatLabel(score) {
  if (score >= 85) return "CRITICAL";
  if (score >= 65) return "HIGH";
  if (score >= 45) return "MEDIUM";
  return "STABLE";
}

function daysUntil(dateStr) {
  return Math.max(0, Math.ceil((new Date(dateStr) - new Date()) / 86400000));
}

function Sparkline({ data, color }) {
  const max = Math.max(...data), min = Math.min(...data);
  const w = 64, h = 24, pad = 2;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = pad + ((max - v) / (max - min || 1)) * (h - pad * 2);
    return `${x},${y}`;
  }).join(" ");
  const last = pts.split(" ").pop().split(",");
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function EUFrameworksLanding() {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeTrack, setActiveTrack] = useState("all");
  const [view, setView]               = useState("heatmap");
  const [expanded, setExpanded]       = useState(null);
  const [mounted, setMounted]         = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => { setTimeout(() => setMounted(true), 50); }, []);

  const handleRefresh = () => {
    setLastRefresh(new Date());
    window.location.reload();
  };

  const filtered = activeTrack === "all" ? FRAMEWORKS : FRAMEWORKS.filter(f => f.tracks.includes(activeTrack));
  const sorted   = [...filtered].sort((a, b) => b.heat - a.heat);
  const syncTime = lastRefresh.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                 + " — " + lastRefresh.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />
      <div className="app">
        <Sidebar />
        <div className="main-content">
          <div className="content-area" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#F5F5F5", minHeight: "calc(100vh - 60px)" }}>

            {/* ── HERO SECTION (Matching Compliance Operator) ── */}
            <div style={{
              background: "linear-gradient(135deg, rgba(96, 96, 96, 0.04) 0%, rgba(64, 64, 64, 0.06) 50%, rgba(96, 96, 96, 0.04) 100%)",
              padding: "48px 48px 40px 48px",
              margin: "-40px -40px 32px -40px",
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              position: "relative",
              overflow: "hidden"
            }}>
              {/* Grid pattern overlay */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                background: "linear-gradient(135deg, rgba(0, 82, 204, 0.02) 25%, transparent 25%), linear-gradient(225deg, rgba(0, 82, 204, 0.02) 25%, transparent 25%)",
                backgroundSize: "60px 60px",
                opacity: 0.3
              }} />

              <div style={{ position: "relative", zIndex: 1, maxWidth: "900px" }}>
                <div style={{
                  fontFamily: "'Consolas', 'Monaco', monospace",
                  fontSize: "10px",
                  fontWeight: 500,
                  letterSpacing: "0.12em",
                  color: "#8A8886",
                  marginBottom: "12px",
                  textTransform: "uppercase"
                }}>
                  REGULATORY HEAT MAP
                </div>
                <h1 style={{
                  fontFamily: "'Segoe UI', system-ui, sans-serif",
                  fontSize: "28px",
                  fontWeight: 600,
                  color: "#242424",
                  margin: "0 0 16px 0",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3
                }}>
                  EU Regulatory Framework Intelligence
                </h1>
                <p style={{
                  fontFamily: "'Segoe UI', system-ui, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#605E5C",
                  lineHeight: 1.6,
                  margin: 0,
                  maxWidth: "700px"
                }}>
                  Real-time compliance heat mapping across 8 active EU regulatory frameworks. Track enforcement deadlines, authority oversight, and implementation phases for MiCA, DORA, AML/CFT, and emerging directives.
                </p>
              </div>
            </div>

            {/* Compliance Track Filters */}
            <div style={{ marginBottom: "24px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {TRACKS.map(t => (
                <button key={t.id} onClick={() => setActiveTrack(t.id)} style={{
                  padding: "6px 16px", fontSize: "12px", fontWeight: 500, cursor: "pointer",
                  border: "1px solid", fontFamily: "'Consolas', 'Monaco', monospace",
                  borderRadius: "3px", transition: "all .15s",
                  background: activeTrack === t.id ? "#0052CC" : "#FFFFFF",
                  color: activeTrack === t.id ? "#FFFFFF" : "#605E5C",
                  borderColor: activeTrack === t.id ? "#0052CC" : "#E1E1E1",
                }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* View switcher */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <div style={{ display: "flex", background: "#fff", border: "1px solid #E1E1E1", borderRadius: "3px", padding: "3px", gap: "2px" }}>
                {[["heatmap","Heat Map"],["tracks","Compliance Tracks"],["timeline","Deadline Timeline"]].map(([id, label]) => (
                  <button key={id} onClick={() => setView(id)} style={{
                    padding: "6px 14px", borderRadius: "2px",
                    fontSize: "11.5px", fontWeight: 500, cursor: "pointer",
                    border: "none", fontFamily: "'Segoe UI', system-ui, sans-serif",
                    background: view === id ? "#eff6ff" : "transparent",
                    color: view === id ? "#0052CC" : "#605E5C",
                    transition: "all .12s",
                  }}>{label}</button>
                ))}
              </div>
              <button
                onClick={handleRefresh}
                style={{
                  fontSize: "11.5px", color: "#605E5C", background: "#fff",
                  border: "1px solid #E1E1E1", borderRadius: "3px",
                  padding: "6px 12px", cursor: "pointer",
                  fontFamily: "'Segoe UI', system-ui, sans-serif",
                  transition: "border-color .15s, color .15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#0052CC"; e.currentTarget.style.color = "#0052CC"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#E1E1E1"; e.currentTarget.style.color = "#605E5C"; }}
              >↻ Refresh Data</button>
            </div>

            {/* ── HEAT MAP VIEW ── */}
            {view === "heatmap" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: "16px" }}>
                  {sorted.map((f, idx) => {
                    const c = heatColor(f.heat);
                    const label = heatLabel(f.heat);
                    const isExpanded = expanded === f.id;
                    return (
                      <div key={f.id} onClick={() => setExpanded(isExpanded ? null : f.id)} style={{
                        background: "#FFFFFF", border: `1px solid ${c.border}`, borderRadius: "3px",
                        padding: "20px", cursor: "pointer", transition: "all .15s", position: "relative",
                        opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(6px)",
                        transitionDelay: `${idx * 50}ms`,
                        boxShadow: isExpanded ? "0 4px 12px rgba(0, 82, 204, 0.12)" : "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 82, 204, 0.12)"}
                        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                      >
                        {/* Left indicator */}
                        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "3px", background: c.bar }} />

                        {/* Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                              <span style={{
                                fontFamily: "'Consolas', 'Monaco', monospace",
                                fontSize: "11px", fontWeight: 600, color: c.text,
                                background: c.bg, padding: "3px 8px", borderRadius: "2px",
                                border: `1px solid ${c.border}`
                              }}>
                                {f.code}
                              </span>
                              <span style={{
                                fontFamily: "'Consolas', 'Monaco', monospace",
                                fontSize: "9px", fontWeight: 600, color: "#8A8886",
                                background: "#F3F2F1", padding: "3px 8px", borderRadius: "2px",
                                textTransform: "uppercase", letterSpacing: "0.5px"
                              }}>
                                {f.status}
                              </span>
                            </div>
                            <h3 style={{
                              fontFamily: "'Segoe UI', system-ui, sans-serif",
                              fontSize: "14px", fontWeight: 600, color: "#242424",
                              margin: "0 0 8px 0", lineHeight: 1.3
                            }}>
                              {f.name}
                            </h3>
                          </div>
                          <Sparkline data={f.trend} color={c.bar} />
                        </div>

                        {/* Premium Metrics Grid */}
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px",
                          marginBottom: "12px",
                          padding: "12px",
                          background: "#FAFAFA",
                          borderRadius: "2px",
                          border: "1px solid #F3F2F1"
                        }}>
                          <div>
                            <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "4px" }}>ENFORCEMENT</div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#242424", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{f.enforcementDate}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "4px" }}>PHASE</div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#242424", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{f.phase}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "4px" }}>COMPLEXITY</div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#242424", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{f.complexity}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "4px" }}>SCOPE</div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#242424", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{f.scope}</div>
                          </div>
                        </div>

                        {/* Heat Score Bar */}
                        <div style={{ marginBottom: "12px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                            <span style={{ fontSize: "10px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px" }}>HEAT SCORE</span>
                            <span style={{ fontSize: "11px", fontFamily: "'Consolas', 'Monaco', monospace", fontWeight: 600, color: c.text }}>{label} — {f.heat}</span>
                          </div>
                          <div style={{ height: "6px", background: c.trackBg, borderRadius: "3px", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${f.heat}%`, background: c.bar, transition: "width .3s" }} />
                          </div>
                        </div>

                        {/* Next Deadline */}
                        <div style={{
                          padding: "10px 12px",
                          background: "#FFFBEB",
                          border: "1px solid #FDE68A",
                          borderRadius: "2px",
                          marginBottom: isExpanded ? "12px" : 0
                        }}>
                          <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#92400E", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "4px" }}>NEXT DEADLINE</div>
                          <div style={{ fontSize: "12px", fontWeight: 500, color: "#78350F", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{f.nextDeadline}</div>
                          <div style={{ fontSize: "11px", color: "#92400E", fontFamily: "'Segoe UI', system-ui, sans-serif", marginTop: "2px" }}>{f.deadlineLabel}</div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div style={{ animation: "fadeIn .2s", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #F3F2F1" }}>
                            <div style={{ fontSize: "11px", color: "#605E5C", lineHeight: 1.6, marginBottom: "12px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                              {f.desc}
                            </div>
                            <div style={{ marginBottom: "12px" }}>
                              <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "6px" }}>REGULATORY DRIVERS</div>
                              {f.drivers.map((d, i) => (
                                <div key={i} style={{ fontSize: "11px", color: "#605E5C", marginBottom: "4px", paddingLeft: "12px", position: "relative", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                                  <span style={{ position: "absolute", left: 0, color: "#0052CC" }}>•</span>
                                  {d}
                                </div>
                              ))}
                            </div>
                            <div style={{ marginBottom: "12px" }}>
                              <div style={{ fontSize: "9px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "6px" }}>OVERSIGHT AUTHORITIES</div>
                              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                {f.authorities.map(auth => (
                                  <span key={auth} style={{ fontSize: "10px", fontFamily: "'Consolas', 'Monaco', monospace", background: "#F3F2F1", color: "#242424", padding: "4px 8px", borderRadius: "2px", fontWeight: 500 }}>
                                    {auth}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <a href={f.url} target="_blank" rel="noopener noreferrer" style={{
                              display: "inline-block", fontSize: "11px", color: "#0052CC", textDecoration: "none",
                              fontWeight: 500, padding: "8px 16px", border: "1px solid #0052CC", borderRadius: "2px",
                              transition: "all .15s", fontFamily: "'Segoe UI', system-ui, sans-serif"
                            }}
                              onMouseEnter={e => { e.currentTarget.style.background = "#0052CC"; e.currentTarget.style.color = "#FFFFFF"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#0052CC"; }}
                            >
                              View Official Source →
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── COMPLIANCE TRACKS VIEW ── */}
            {view === "tracks" && (
              <div style={{ background: "#FFFFFF", border: "1px solid #E1E1E1", borderRadius: "3px", padding: "24px" }}>
                <p style={{ fontSize: "13px", color: "#605E5C", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                  Select a compliance track above to filter frameworks by business profile.
                </p>
              </div>
            )}

            {/* ── DEADLINE TIMELINE VIEW ── */}
            {view === "timeline" && (
              <>
                <div style={{ background: "#FFFFFF", border: "1px solid #E1E1E1", borderRadius: "3px", padding: "20px", marginBottom: "16px" }}>
                  <div style={{ fontSize: "10px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "12px" }}>UPCOMING REGULATORY MILESTONES</div>
                  {DEADLINES.map((d, idx) => {
                    const days = daysUntil(d.date);
                    const c = heatColor(d.heat);
                    return (
                      <div key={idx} style={{
                        display: "flex", alignItems: "center", gap: "16px",
                        padding: "12px", borderRadius: "2px", marginBottom: "8px",
                        background: d.urgent ? "#FFFBEB" : "#FAFAFA",
                        border: `1px solid ${d.urgent ? "#FDE68A" : "#F3F2F1"}`,
                        transition: "all .15s"
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = d.urgent ? "#FEF3C7" : "#F3F2F1"}
                        onMouseLeave={e => e.currentTarget.style.background = d.urgent ? "#FFFBEB" : "#FAFAFA"}
                      >
                        <div style={{
                          minWidth: "80px",
                          fontFamily: "'Consolas', 'Monaco', monospace",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#242424"
                        }}>
                          {new Date(d.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                        <div style={{
                          width: "2px",
                          height: "32px",
                          background: c.bar
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "12px", fontWeight: 500, color: "#242424", marginBottom: "4px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                            {d.label}
                          </div>
                          <div style={{ fontSize: "10px", fontFamily: "'Consolas', 'Monaco', monospace", color: "#8A8886" }}>
                            {d.framework} • {days}d remaining
                          </div>
                        </div>
                        <div style={{
                          fontFamily: "'Consolas', 'Monaco', monospace",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: days < 90 ? "#DC2626" : days < 180 ? "#EA580C" : "#605E5C",
                          minWidth: "50px",
                          textAlign: "right"
                        }}>
                          {days}d
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Footer */}
            <div style={{ marginTop: "32px", paddingTop: "14px", borderTop: "1px solid #E1E1E1", display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#8A8886", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
              <span>DATA SOURCE: Official EU regulatory authorities — Sentinel Operator</span>
              <span>Last synced: {syncTime}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
